import React, { useCallback, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { AIcon } from '../../atoms/AIcon';
import { ASubheader, ASubheaderSize, ASubheaderTag } from '../../atoms/ASubheader';
import { MIconInteractive } from '../MIconInteractive';
import { ALinkStandalone, ALinkStandaloneSize } from '../../atoms/ALinkStandalone';
import { MButton, MButtonIconPlacement, MButtonSize, MButtonType } from '../MButton';
import { SNACKBAR_MODAL_TYPE, type MSnackbarProps, type SnackbarCTA } from './MSnackbar.types';
import * as Styled from './MSnackbar.styles';

// Then in the component render:
export const COMPONENT_NAME = 'm-web-ui-kit-MSnackbar';
export const TEST_ID = 'web-ui-kit-MSnackbar';

export const MSnackbar: React.FC<MSnackbarProps> = ({
  modalType,
  successMessage,
  errorMessage,
  ctas: propCtas,
  autoHideDuration: propAutoHideDuration = 5000,
  iconClass: propIconClass,
  className,
  subheaderSize = ASubheaderSize.M,
  subheaderElement = ASubheaderTag.h3,
  closeAriaLabel = 'Close',
  onClose,
  usePortal = false,
  closeTrackingProperties,
  closeCustomClickTrackValue,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const isSuccess = modalType === SNACKBAR_MODAL_TYPE.SUCCESS;
  const displayMessage = (isSuccess ? successMessage : errorMessage) || '';
  const displayCtas: SnackbarCTA[] = (propCtas ?? []).map(cta => ({
    ...cta,
    isExternalLink: cta.isExternalLink ?? false,
  }));
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

  const displayIconClass = propIconClass ?? (isSuccess ? 'icon-heart-fill' : 'icon-alert');

  const handleClose = useCallback(() => {
    setIsOpen(false);
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen || propAutoHideDuration <= 0) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      handleClose();
    }, propAutoHideDuration);

    return () => {
      window.clearTimeout(timer);
    };
  }, [propAutoHideDuration, isOpen, handleClose]);

  useEffect(() => {
    if (usePortal && typeof document !== 'undefined') {
      setPortalContainer(document.body);
    }
  }, [usePortal]);

  const snackbarContent = (
    <Styled.Wrapper
      $isOpen={isOpen}
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      className={clsx(className, 'msnackbar-bottom-center')}
      $isSuccess={!!isSuccess}
      role={isSuccess ? 'status' : 'alert'}
      aria-label={'MSnackbar'}
    >
      <div className="d-flex align-items-center">
        <div className="d-flex flex-column">
          <div className="d-flex align-items-start gap-2">
            <AIcon icon={clsx('icon-inverse icon-m mb-1', displayIconClass)} />
            <div>
              <ASubheader
                className="t-font-inverse-m m-ellipsis-3lines px-2"
                text={displayMessage}
                size={subheaderSize}
                element={subheaderElement}
              />
              {displayCtas.length > 0 && (
                <div className="msnackbar-links d-flex px-2 pt-2">
                  {displayCtas.map((cta, idx) =>
                    cta.isLink ? (
                      <ALinkStandalone
                        key={idx}
                        href={cta.url}
                        text={cta.label}
                        externalLink={cta.isExternalLink}
                        onClick={cta.callback}
                        className="inverse"
                        size={ALinkStandaloneSize.ExtraSmall}
                      />
                    ) : (
                      <MButton
                        key={idx}
                        className="t-font-inverse-xs"
                        buttonType={MButtonType.Tertiary}
                        size={MButtonSize.S}
                        iconPlacement={MButtonIconPlacement.None}
                        buttonText={cta.label}
                        onClick={cta.callback}
                      />
                    )
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <MIconInteractive
        aIcon={{ icon: 'icon-inverse icon-clear' }}
        ariaLabel={closeAriaLabel}
        onClick={handleClose}
        trackingProperties={closeTrackingProperties}
        custom_click_track_value={closeCustomClickTrackValue}
      />
    </Styled.Wrapper>
  );
  // If usePortal is true, render the snackbar in a portal
  if (usePortal && !portalContainer) return null;
  if (usePortal && portalContainer) return createPortal(snackbarContent, portalContainer);
  return snackbarContent;
};
